import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";

import TableFilters from "../tmt/TableFilters";
import TableActionButtons from "../tmt/TableActionButtons";
import CustomSnackbar from "../common/CustomSnackbar";
import { DRM_COLUMN } from "../../constData/columnDrmt";
import DrmTable from "../tmt/DrmTable";
import { useExportExcelTableData } from "../../customHook/useExportExcelTableData";
import { snackBars, defaultFilter, exportToExcel, stringify, mapRateToChargeFields, modecreation } from "../../helpers";
import CustomButton from "../common/CustomButton";
import FileImportCard from "../tmt/FileImportCard";
import DialogConformationMsg from "../tmt/DialogConformationMsg";
import { useCoupaFileUpload } from "../../customHook/useCoupaFileUpload";
import { useBidTableOperations } from "../../customHook/useBidTableOperations";
import { useDrmFilters } from "../../customHook/useDrmFilters";
import { useDrmData } from "../../customHook/useDrmData";
import { useDialogStates } from "../../customHook/useDialogStates";
import { useDrmColumnManagement } from "../../customHook/useDrmColumnManagement";
import { DIALOG_TYPES } from "../../customHook/useDialogStates";
import { useDrmFilterUrlSync } from "../../customHook/useDrmFilterUrlSync";
import { DRMT_EXCEL_LABEL } from "../../constData/excelColumn";
import { setDrmTableFilter as setBidTableFilter } from "../../redux/reducers/drmTableFilterSlice";
import { drmtFilterPayload } from "../../constData/filterRequestPayloadDrmt";

import "./BidMaintenance.sass";
import { getColumView } from "../../redux/reducers/drmCustomColumnSlice";
import { clearModifications } from "../../redux/reducers/drmTableDataSlice";
import { ENUM_DATA } from "../../constData/enum";

const INITIAL_TABLE_STATE = {
  headers: [],
  toggleProps: null,
  density: "small",
  currentPage: 1,
};

const INITIAL_FILTER_DATA = {
  division: [],
  OCountry: [],
  DCountry: [],
};

const ActionButton = ({ datatestid, onClick, disabled, children, ...props }) => (
  <CustomButton
    datatestid={datatestid}
    className="bid_buttons"
    size="small"
    variant="outlined"
    onClick={onClick}
    disabled={disabled}
    btnType="nonary"
    {...props}
  >
    {children}
  </CustomButton>
);

const TABLE_TYPE = "drmBidTableData";

function BidMaintenance() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { tableFilter } = useSelector((state) => state.drmFilterPayload);
  const tableFilterRef = useRef(tableFilter);
  tableFilterRef.current = tableFilter;


  const { defaultColumns, customViews } = useSelector((state) => state.customColumn);
  const { drmBidTableData: drmTableData, isLoading: isDrmBidLoading } = useSelector((state) => state?.drmTableData || {});
  const { masterData } = useSelector((state) => state.masterData);
  const { userInfo } = useSelector((state)=>state.user);
  const modifiedRows = drmTableData?.modifiedRows || {};
  const selectedRows = drmTableData?.selectedRows || [];
  const { coupaFile, fileUploadedStatus } = useSelector((state) => state.drmCoupaFile);
  const { successMessage: updateSuccessMsg, isLoading: updateIsLoading} = useSelector((state) => state?.drmUpdates || {});
  const { successMessage: deleteSuccessMsg, isLoading: deleteIsLoading } = useSelector((state) => state?.drmDelete || {});
  const { successMessage: submitSuccessMsg, isLoading: submitIsLoading } = useSelector((state) => state?.drmSubmit || {});
  const { successMessage: expireSuccessMsg, isLoading: expireIsLoading } = useSelector((state) => state?.drmExpire || {});

  const [bidTableState, setBidTableState] = useState(INITIAL_TABLE_STATE);
  const [bidFilterData, setBidFilterData] = useState(INITIAL_FILTER_DATA);


  const countryOptions = useMemo(
    () => masterData?.countries?.data || [],
    [masterData?.countries]
  );

    useDrmFilterUrlSync({
    scope: "bid",
    filterState: tableFilter,
    dispatch,
    setFilterAction: setBidTableFilter,
    uiState: bidFilterData,
    setUiState: setBidFilterData,
    initialUiState: INITIAL_FILTER_DATA,
    defaultFilterState: drmtFilterPayload,
    countryOptions,
  });


  const { allColumnsWithStatus, toggleColumnVisibility, handleColumnReorder, orderedColumns: managedOrderedColumns } = useDrmColumnManagement(1, defaultColumns.bid, customViews.bid);

  const { saveState, setSaveState, submitState, setSubmitState, deleteState, setDeleteState, expireState, setExpireState, closeDialog } = useDialogStates();

  const { handleDestinationCity, handleOriginCity, handleDivision } = useDrmFilters(tableFilterRef, dispatch); 
  const { handleFilterTableSearch, handleClearFilterTableSearch } = useDrmData(dispatch);
  const { apiSnackbar, apiSnackClose, handleSaveConfirm, handleSubmitConfirm, handleDeleteConfirm, handleExpireConfirm } = useBidTableOperations(dispatch, drmTableData);

  const { handleDownloadRecodes, handleClose, snackbarState } = useExportExcelTableData();
  const { fileImportRef, isConfirmationOpen, snackBar, handleSnackClose, handleImportClick, handleFileImport, handleOpenConformationMsg, handleCloseConformationMsg, handleConfirm } = useCoupaFileUpload();

  const allowedBidKeys = useMemo(() => DRMT_EXCEL_LABEL.map((n) => ({key: n.key, name: n.name})),[]);
  const handleDownLoadBidTableRecords = () => {
    if (selectedRows.length > 0) {
      const formatted = selectedRows.map((item) => {
        const original = item?.original;
        const record = { ...original, ...mapRateToChargeFields(original), mode: modecreation(original?.tariffId), qtyBucketType: original?.bucketType, bucketType: ENUM_DATA.Empty };
        return Object.fromEntries(allowedBidKeys.map(({key, name}) => [name, stringify(record[key], key) || ""]));
      });
      exportToExcel(formatted, "SelectedBidExcelExport");
    } else {
      handleDownloadRecodes();
    }
  };

  const handleColumnOrderBid = useCallback((action, columnName) => {
    handleColumnReorder(columnName, action);
  }, [handleColumnReorder]);

  const handleDestinationCityWithState = useCallback(
    (value) => {
      handleDestinationCity(value);
      setBidFilterData((prev) => ({ ...prev, DCountry: value }));
    },
    [handleDestinationCity]
  );

  const handleDivisionWithState = useCallback(
    (value) => {
      handleDivision(value);
      setBidFilterData((prev) => ({ ...prev, division: value }));
    },
    [handleDivision]
  );

  const handleOriginCityWithState = useCallback(
    (value) => {
      handleOriginCity(value);
      setBidFilterData((prev) => ({ ...prev, OCountry: value }));
    },
    [handleOriginCity]
  );
  
  const handleFilterSearch = useCallback(() => {
    handleFilterTableSearch(bidTableState.currentPage, tableFilter, undefined, TABLE_TYPE);
  }, [handleFilterTableSearch, bidTableState.currentPage, tableFilter]);

  const handleRefresh = useCallback(() => {
    handleClearFilterTableSearch(bidTableState.currentPage, tableFilterRef.current, undefined, TABLE_TYPE);
  }, [handleClearFilterTableSearch, bidTableState.currentPage]);

  const handlePageRefresh = useCallback(
    (page, type) => {
      handleFilterTableSearch(page, tableFilterRef.current, type, TABLE_TYPE);
    },
    [handleFilterTableSearch, bidTableState.currentPage, tableFilter, tableFilterRef]
  );

  const handleDeleteButtonClicked = useCallback(() => {
    setDeleteState({
      open: true,
      dialogTitle: t("delete_dialog_title"),
      dialogContentText: t("delete_dialog_content"),
      cancel: t("cancel"),
      confirm: t("delete_btn"),
    });
  }, [setDeleteState]);

  const handleExpireButtonClicked = useCallback(() => {
    setExpireState({
      open: true,
      dialogTitle: t("expire_dialog_title"),
      dialogContentText: t("expire_dialog_content"),
      cancel: t("cancel"),
      confirm: t("expire_btn"),
    })
  }, [setExpireState])

  const handleSaveButtonClicked = useCallback(() => {
    const modifiedRowsCount = Object.keys(modifiedRows).length;
    setSaveState({
      open: true,
      dialogTitle: t("save_dialog_title"),
      dialogContentText: `${t("save_dialog_content")} ${modifiedRowsCount} row${modifiedRowsCount !== 1 ? "s" : ""}`,
      cancel: t("cancel"),
      confirm: t("proceed"),
    });
  }, [modifiedRows, setSaveState]);

  const handleSubmitButtonClicked = useCallback(() => {
    setSubmitState({
      open: true,
      dialogTitle: t("submit_dialog_title"),
      dialogContentText: t("submit_dialog_content"),
      cancel: t("cancel"),
      confirm: t("submit"),
    });
  }, [setSubmitState]);

  useEffect(() => {
    if (isDrmBidLoading) {
      dispatch(clearModifications({ tableType: "drmBidTableData" }));
    }
  }, [isDrmBidLoading]);

  useEffect(() => {
    if (
      (!updateIsLoading && updateSuccessMsg != undefined) ||
      (!deleteIsLoading && deleteSuccessMsg != undefined) ||
      (!submitIsLoading && submitSuccessMsg != undefined) ||
      (!expireIsLoading && expireSuccessMsg != undefined)
    ) {
      dispatch(clearModifications({ tableType: "drmBidTableData" }));
      handlePageRefresh(INITIAL_TABLE_STATE.currentPage, defaultFilter);
    }
  }, [updateSuccessMsg, updateIsLoading, deleteSuccessMsg, deleteIsLoading, submitSuccessMsg, submitIsLoading, expireSuccessMsg, expireIsLoading, handlePageRefresh]);

  useEffect(() => {
    const controller = new AbortController();
    const body = {
      viewCodes: [],
      viewNames: [],
      viewCategory: ["1"],
      isBookMark: [false, true],
      userCode: [userInfo?.userName],
    }
    dispatch(getColumView({ body , signal: controller.signal}))
     return () =>{
       controller.abort();
     }
  },[userInfo])

  const handleSaveConfirmWithClose = useCallback(() => {
    handleSaveConfirm();
    closeDialog(DIALOG_TYPES.SAVE);
  }, [handleSaveConfirm, closeDialog, bidTableState.currentPage, defaultFilter]);

  const handleSubmitConfirmWithClose = useCallback(() => {
      handleSubmitConfirm();
      closeDialog(DIALOG_TYPES.SUBMIT);
  }, [handleSubmitConfirm, closeDialog]);

  const handleExpireButtonConfirmWithClose = useCallback(() => {
    handleExpireConfirm();
    closeDialog(DIALOG_TYPES.EXPIRE);
  }, [handleExpireConfirm, closeDialog]);

  const handleChangeLogHistory = () => {
    navigate("/dashboard/drm_tool/audit_history?process=1", { replace: true });
  };

  const handleDeleteCoupaFile = () => {
    navigate("/dashboard/drm_tool/coupa_file_management", { replace: true });
  };

  const handleDeleteConfirmWithClose = useCallback(() => {
    handleDeleteConfirm();
    closeDialog(DIALOG_TYPES.DELETE);
  }, [handleDeleteConfirm, closeDialog]);

  const columnOrder = useMemo(() => {
    const visibleColumnNames = managedOrderedColumns
      .filter(col => col.isVisible)
      .map(col => col.columnName);
    
    return visibleColumnNames
      .map(columnName => DRM_COLUMN.find(col => col.Header === columnName))
      .filter(Boolean);
  }, [managedOrderedColumns]);

  const tableData = useMemo(() => drmTableData?.drmMaintenanceDetails || [], [drmTableData?.drmMaintenanceDetails]);

  const hasSelectedRows = selectedRows.length > 0;
  const hasModifiedRows = Object.keys(modifiedRows).length > 0;
  const actionButtons = [
    {
      id: "import",
      dataTestId: "import",
      ariaLabel: "Import file",
      buttonText: t("import"),
      accessor: "import",
      onClick: handleImportClick,
      disabled: !!fileUploadedStatus,
      hasFileInput: true,
    },
    {
      id: "delete",
      dataTestId: "delete",
      ariaLabel: "Delete selected rows",
      buttonText: t("delete_btn"),
      accessor: "delete",
      onClick: handleDeleteButtonClicked,
      disabled: !hasSelectedRows,
    },
    {
      id: "expire",
      dataTestId: "expire",
      ariaLabel: "Expire selected rows",
      buttonText: t("expire_btn"),
      accessor: "expire",
      onClick: handleExpireButtonClicked,
      disabled: !hasSelectedRows,
    },
    {
      id: "save",
      dataTestId: "save",
      ariaLabel: "Save changes",
      buttonText: t("validate"),
      accessor: "save",
      onClick: handleSaveButtonClicked,
      disabled: !hasModifiedRows,
    },
    {
      id: "submit",
      dataTestId: "submit",
      ariaLabel: "Submit data",
      buttonText: t("submit"),
      accessor: "submit",
      onClick: handleSubmitButtonClicked,
      disabled: !hasSelectedRows,
    },
  ];
  const confirmationDialogs = [
    {
      id: "fileImport",
      open: isConfirmationOpen,
      handleClose: handleCloseConformationMsg,
      handleConfirm: handleConfirm,
      dialogTitle: `${t("import_dialog_title")} ${coupaFile.coupaFileName}`,
      dialogContentText: t("import_dialog_content"),
      cancel: t("remove"),
      confirm: t("proceed"),
    },
    {
      id: "save",
      open: saveState.open,
      handleClose: () => closeDialog(DIALOG_TYPES.SAVE),
      handleConfirm: handleSaveConfirmWithClose,
      dialogTitle: saveState.dialogTitle,
      dialogContentText: saveState.dialogContentText,
      cancel: saveState.cancel,
      confirm: saveState.confirm,
    },
    {
      id: "submit",
      open: submitState.open,
      handleClose: () => closeDialog(DIALOG_TYPES.SUBMIT),
      handleConfirm: handleSubmitConfirmWithClose,
      dialogTitle: submitState.dialogTitle,
      dialogContentText: submitState.dialogContentText,
      cancel: submitState.cancel,
      confirm: submitState.confirm,
    },
    {
      id: "expire",
      open: expireState.open,
      handleClose: () => closeDialog(DIALOG_TYPES.EXPIRE), 
      handleConfirm: handleExpireButtonConfirmWithClose, 
      dialogTitle: expireState.dialogTitle,
      dialogContentText: expireState.dialogContentText,
      cancel: expireState.cancel,
      confirm: expireState.confirm,
    },
    {
      id: "delete",
      open: deleteState.open,
      handleClose: () => closeDialog(DIALOG_TYPES.DELETE),
      handleConfirm: handleDeleteConfirmWithClose,
      dialogTitle: deleteState.dialogTitle,
      dialogContentText: deleteState.dialogContentText,
      cancel: deleteState.cancel,
      confirm: deleteState.confirm,
    },
  ];

  return (
    <Grid container className="drmtBidContainer">
      <Stack spacing={0.5} alignItems="flex-start" width="100%" px={2}>

        <Stack direction="row" justifyContent="space-between" width="100%">
          <Stack alignItems="flex-start" width="70%">
            <TableFilters
              pageType="bid_maintenance"
              multipleEnabled={false}
              filterData={bidFilterData}
              handleDivision={handleDivisionWithState}
              handleOriginCity={handleOriginCityWithState}
              handleDestinationCity={handleDestinationCityWithState}
              handleFilterSearch={handleFilterSearch}
            />
          </Stack>
          <Stack alignItems="flex-end" width="29%">
            <TableActionButtons
              tableState={bidTableState}
              setTableState={setBidTableState}
              handleColumnOrder={handleColumnOrderBid}
              handleChangeLogHistory={handleChangeLogHistory}
              handleDownloadRecodes={handleDownLoadBidTableRecords}
              handleDeleteCoupaFile={handleDeleteCoupaFile}
              handleRefresh={handlePageRefresh}
              allColumnsWithStatus={allColumnsWithStatus}
              onToggleColumnVisibility={toggleColumnVisibility}
            />
          </Stack>
        </Stack>

        {fileUploadedStatus && (
          <FileImportCard
            fileName={coupaFile.coupaFileName}
            onProcessClick={handleOpenConformationMsg}
            onClose={handleCloseConformationMsg}
          />
        )}

        <DrmTable
          tableName="Bid"
          tableData={tableData}
          columnOrder={columnOrder}
          tableState={bidTableState}
          editableRow={true}
          onPage={drmTableData?.onPage}
          totalPages={drmTableData?.totalPages}
          totalRecords={drmTableData?.totalRecords}
          isLoadingDrmTable={isDrmBidLoading}
          setTableState={setBidTableState}
          handleRefresh={handlePageRefresh}
          handleClear={handleRefresh}
        />

        <Stack direction="row" justifyContent="flex-end" spacing={1} width="100%" px={0}>
          {actionButtons.map((actionBtn) => (
            <ActionButton
              key={actionBtn.id}
              datatestid={actionBtn.dataTestId}
              onClick={actionBtn.onClick}
              disabled={actionBtn.disabled}
              ariaLabel={actionBtn.ariaLabel}
            >
              {actionBtn.hasFileInput && <input type="file" ref={fileImportRef} onChange={handleFileImport} style={{ display: "none" }} />}
              {actionBtn.buttonText}
            </ActionButton>
          ))}
        </Stack>

        {snackBar.open && (
          <CustomSnackbar
            open={snackBar.open}
            severity={snackBar.severity}
            autoHideDuration={snackBar.autoHideDuration}
            datatestId="invalid-update"
            message={snackBar.message}
            handleClose={handleSnackClose}
          />
        )}

        {apiSnackbar.open && (
          <CustomSnackbar
            open={apiSnackbar.open}
            severity={apiSnackbar.severity}
            autoHideDuration={6000}
            datatestId="button-update"
            message={apiSnackbar.message}
            handleClose={apiSnackClose}
          />
        )}

        {confirmationDialogs.map((dialog) => (
          <DialogConformationMsg
            key={dialog.id}
            open={dialog.open}
            handleClose={dialog.handleClose}
            handleConfirm={dialog.handleConfirm}
            dialogTitle={dialog.dialogTitle}
            dialogContentText={dialog.dialogContentText}
            cancel={dialog.cancel}
            confirm={dialog.confirm}
          />
        ))}

        {snackBars.map((snackbar) => (
          <CustomSnackbar
            key={snackbar.id}
            open={snackbarState?.[snackbar.handleAccessor]}
            severity={snackbar.severity}
            autoHideDuration={snackbar.autoHideDuration}
            datatestId={snackbar.datatestId}
            message={t(snackbar.message)}
            handleClose={() => handleClose(snackbar.handleAccessor)}
          />
        ))}
      </Stack>
    </Grid>
  );
}

export default BidMaintenance;